import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, COMPANY } from "../data/content";

const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center">
    <img src="/kotam-logo.png" alt="Kotam Projects" className="h-11 sm:h-12 w-auto rounded-[6px]" />
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-white/75 backdrop-blur-xl border-b border-black/5 py-3" : "bg-transparent py-5"
      }`}
    >
      <nav className="container-px flex items-center justify-between">
        <Logo />

        <div className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`relative text-sm font-medium tracking-wide transition-colors ${
                scrolled
                  ? pathname === l.to ? "text-forest" : "text-charcoal/70 hover:text-forest"
                  : pathname === l.to ? "text-white" : "text-white/75 hover:text-white"
              }`}
            >
              {l.label}
              {pathname === l.to && (
                <motion.span layoutId="nav-underline" className="absolute -bottom-1.5 left-0 right-0 h-[2px] bg-gold" />
              )}
            </Link>
          ))}
        </div>

        <div className="hidden lg:block">
          <Link
            to="/contact"
            data-testid="nav-cta-quote"
            className="group inline-flex items-center gap-2 rounded-full bg-forest px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-gold hover:text-forest-dark"
          >
            Request a Quote
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden flex h-11 w-11 items-center justify-center rounded-full bg-forest text-white"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-black/5"
          >
            <div className="container-px py-6 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="py-3 text-lg font-medium text-charcoal border-b border-black/5"
                >
                  {l.label}
                </Link>
              ))}
              <Link
                to="/contact"
                data-testid="nav-mobile-cta"
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-forest px-6 py-4 text-base font-semibold text-white"
              >
                Request a Quote <ArrowUpRight className="h-4 w-4" />
              </Link>
              <a href={`tel:${COMPANY.phoneRaw}`} className="mt-2 text-center text-sm text-charcoal/60">
                {COMPANY.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
